/**
 * OperatorSettingsTab — self-serve feature toggles for the operator's own
 * storefront. Backed by GET/PUT /api/operator/features.
 *
 * Admin still has override authority via /admin/operators/{op_id}/features
 * — both sides write to the same `feature_flags` dict.
 *
 * Layout:
 *  - Cycle reminder card
 *  - Feature toggles grouped: Sportsbook / Casino / Lottery (with per-state
 *    Illinois/Mississippi/Tennessee sub-toggles indented under it) / Other
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Switch } from './ui/switch';
import { Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const LOTTERY_STATE_KEYS = ['lottery_il', 'lottery_ms', 'lottery_tn'];

const OperatorSettingsTab = () => {
    const [features, setFeatures] = useState(null);
    const [catalog, setCatalog]   = useState([]);
    const [saving, setSaving]     = useState(null); // key being saved
    const [loading, setLoading]   = useState(true);

    useEffect(() => {
        axios.get(`${API}/operator/features`, { withCredentials: true })
            .then(({ data }) => {
                setFeatures(data.features || {});
                setCatalog(data.catalog || []);
            })
            .catch(() => setFeatures({}))
            .finally(() => setLoading(false));
    }, []);

    const toggle = async (key, value) => {
        if (!features) return;
        const next = { ...features, [key]: value };
        setFeatures(next);
        setSaving(key);
        try {
            await axios.put(`${API}/operator/features`,
                { features: next }, { withCredentials: true });
            toast.success(`${labelFor(key, catalog)} ${value ? 'enabled' : 'disabled'}`);
        } catch (err) {
            // revert on failure
            setFeatures(features);
            toast.error(err.response?.data?.detail || 'Failed to update');
        } finally {
            setSaving(null);
        }
    };

    if (loading) return (
        <div className="text-center py-8 text-zinc-500" data-testid="operator-settings-loading">
            <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading…
        </div>
    );
    if (!features) return null;

    // Split catalog: lottery parent + per-state toggles handled together
    const lotteryParent = catalog.find(c => c.key === 'lottery');
    const lotteryStates = catalog.filter(c => LOTTERY_STATE_KEYS.includes(c.key));
    const others = catalog.filter(c =>
        c.key !== 'lottery' && !LOTTERY_STATE_KEYS.includes(c.key)
    );

    return (
        <div className="space-y-4" data-testid="operator-settings-tab">
            <div className="rounded-md border border-zinc-800 bg-zinc-900/30 p-5">
                <h3 className="text-sm font-semibold text-zinc-100">Location features</h3>
                <p className="text-xs text-zinc-500 mt-0.5">
                    Toggle features on/off for this storefront. Players at this location only
                    see features that are ON. Admin can override these from the platform console.
                </p>

                <div className="mt-5 space-y-2">
                    {/* Lottery group first */}
                    {lotteryParent && (
                        <div className="space-y-1.5">
                            <FeatureRow
                                item={lotteryParent}
                                on={features[lotteryParent.key] !== false}
                                onChange={(v) => toggle(lotteryParent.key, v)}
                                saving={saving === lotteryParent.key}
                            />
                            <div className="pl-6 space-y-1.5">
                                {lotteryStates.map(s => (
                                    <FeatureRow
                                        key={s.key}
                                        item={s}
                                        indented
                                        disabled={features.lottery === false}
                                        on={features[s.key] !== false}
                                        onChange={(v) => toggle(s.key, v)}
                                        saving={saving === s.key}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Everything else */}
                    {others.map(c => (
                        <FeatureRow
                            key={c.key}
                            item={c}
                            on={features[c.key] !== false}
                            onChange={(v) => toggle(c.key, v)}
                            saving={saving === c.key}
                        />
                    ))}
                </div>
            </div>

            <div className="rounded-md border border-zinc-800 bg-zinc-900/30 p-5">
                <h3 className="text-sm font-semibold text-zinc-100">Settlement cycle</h3>
                <p className="text-xs text-zinc-500 mt-0.5">
                    Mondays → Sundays (America/Chicago). Net house position is settled with the platform weekly.
                </p>
            </div>
        </div>
    );
};

const FeatureRow = ({ item, on, onChange, saving, indented = false, disabled = false }) => (
    <div className={`flex items-center justify-between bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2.5
        ${indented ? 'border-zinc-800/60' : ''}`}
        data-testid={`feature-row-${item.key}`}>
        <div className="min-w-0">
            <p className="text-sm text-zinc-100 font-medium">{item.label}</p>
            {item.desc && <p className="text-[11px] text-zinc-500 mt-0.5 leading-tight">{item.desc}</p>}
        </div>
        <div className="flex items-center gap-2 pl-3">
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin text-zinc-500" />}
            <Switch
                checked={!!on}
                disabled={disabled || !!saving}
                onCheckedChange={onChange}
                className="data-[state=unchecked]:!bg-zinc-700 data-[state=checked]:!bg-emerald-500 border-zinc-500 [&>span]:!bg-white"
                data-testid={`feature-toggle-${item.key}`}
            />
        </div>
    </div>
);

const labelFor = (key, catalog) =>
    catalog.find(c => c.key === key)?.label || key;

export default OperatorSettingsTab;
